import React, { useState } from 'react';
import { Card, Typography, Tag } from 'antd';
import { EnvironmentOutlined, ClockCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { MapContainer, Marker, Popup, Polyline } from 'react-leaflet';
import YandexTileLayer from '../../../shared/components/maps/YandexTileLayer';
import { LocationLog, ActivityWithLocations, ActivityType } from '../../../../types/field.types';
import dayjs from 'dayjs';
import L from 'leaflet';

const { Title, Text } = Typography;

// Custom marker ikonları
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const blueIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Route noktaları için küçük gri marker
const routePointIcon = L.divIcon({
  html: `<div style="
    background-color:rgb(83, 83, 83);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  "></div>`,
  className: '',
  iconSize: [8, 8],
  iconAnchor: [4, 4],
  popupAnchor: [0, -4]
});

interface RouteMapProps {
  locationRoute: LocationLog[];
  activities?: ActivityWithLocations[];
}

// Aktivite için random renk üret (ama aynı aktivite için her zaman aynı renk)
const getActivityColor = (activityId: number): string => {
  const colors = [
    '#1890ff', // Mavi
    '#52c41a', // Yeşil
    '#fa8c16', // Turuncu
    '#722ed1', // Mor
    '#eb2f96', // Pembe
    '#13c2c2', // Cyan
    '#faad14', // Altın
    '#f5222d', // Kırmızı
    '#2f54eb', // Geek Blue
    '#a0d911', // Lime
  ];
  
  // activityId'ye göre deterministik renk seç (aynı aktivite her zaman aynı renk)
  return colors[activityId % colors.length];
};

// Aktivite tipi için ikon
const getActivityTypeLabel = (type: ActivityType): string => {
  const labels = {
    [ActivityType.VISIT]: 'Ziyaret',
    [ActivityType.DELIVERY]: 'Teslimat',
    [ActivityType.MAINTENANCE]: 'Bakım',
    [ActivityType.OFFICE]: 'Ofis',
    [ActivityType.OTHER]: 'Diğer'
  };
  return labels[type] || type;
};

// Her aktivite için benzersiz renk marker oluştur
const createActivityIcon = (color: string) => {
  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    className: '',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

const RouteMap: React.FC<RouteMapProps> = ({ locationRoute, activities = [] }) => {
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);

  if (locationRoute.length === 0) return null;

  const handleActivityClick = (activityId: number) => {
    // Aynı aktiviteye tıklanırsa filtreyi kaldır
    if (selectedActivityId === activityId) {
      setSelectedActivityId(null);
    } else {
      setSelectedActivityId(activityId);
    }
  };

  const handleClearFilter = () => {
    setSelectedActivityId(null);
  };

  // Aktivitenin görünür olup olmadığını kontrol et
  const isActivityVisible = (activityId: number) => {
    return selectedActivityId === null || selectedActivityId === activityId;
  };

  // Aktivitelere ait olmayan route noktalarını bul
  const getRouteOnlyLocations = () => {
    // Tüm aktivite location ID'lerini topla
    const activityLocationIds = new Set<number>();
    activities.forEach(activity => {
      activity.locations?.forEach(loc => {
        activityLocationIds.add(loc.id);
      });
    });

    // Route içindeki location'lardan aktiviteye ait olmayanları filtrele
    return locationRoute.filter(loc => !activityLocationIds.has(loc.id));
  };

  const routeOnlyLocations = getRouteOnlyLocations();

  return (
    <Card style={{ marginBottom: '24px' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Title level={5} style={{ margin: 0 }}>
          <EnvironmentOutlined /> Günlük Rota - {locationRoute.length} Nokta ({routeOnlyLocations.length} hareket + {locationRoute.length - routeOnlyLocations.length} aktivite), {activities.length} Aktivite
        </Title>
        {activities.length > 0 && (
          <div className="d-flex gap-2 flex-wrap align-items-center">
            {selectedActivityId !== null && (
              <Tag
                color="red"
                style={{ cursor: 'pointer', marginRight: '8px' }}
                onClick={handleClearFilter}
                icon={<EyeOutlined />}
              >
                Tümünü Göster
              </Tag>
            )}
            {activities.map(activity => {
              const isSelected = selectedActivityId === activity.id;
              const isVisible = isActivityVisible(activity.id);
              
              return (
                <Tag
                  key={activity.id}
                  color={getActivityColor(activity.id)}
                  style={{
                    cursor: 'pointer',
                    opacity: selectedActivityId === null ? 1 : (isSelected ? 1 : 0.4),
                    border: isSelected ? '2px solid #000' : 'none',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => handleActivityClick(activity.id)}
                  icon={isSelected ? <EyeOutlined /> : undefined}
                >
                  {activity.title}
                </Tag>
              );
            })}
          </div>
        )}
      </div>
      
      <div style={{ height: '600px', width: '100%' }}>
        <MapContainer
          center={[
            parseFloat(locationRoute[0].latitude),
            parseFloat(locationRoute[0].longitude)
          ]}
          zoom={13}
          style={{ height: '100%', width: '100%', borderRadius: '8px' }}
        >
          <YandexTileLayer />
          
          {/* Her aktivite için rota çizgisi */}
          {activities.map(activity => {
            if (!activity.locations || activity.locations.length < 2) return null;
            if (!isActivityVisible(activity.id)) return null; // Filtre kontrolü
            
            const activityPositions = activity.locations.map(loc => [
              parseFloat(loc.latitude),
              parseFloat(loc.longitude)
            ] as [number, number]);
            
            const isSelected = selectedActivityId === activity.id;
            
            return (
              <Polyline
                key={`polyline-${activity.id}`}
                positions={activityPositions}
                pathOptions={{
                  color: getActivityColor(activity.id),
                  weight: isSelected ? 6 : 4, // Seçili ise daha kalın
                  opacity: isSelected ? 1 : 0.8
                }}
              />
            );
          })}
          
          {/* Tüm rota çizgisi (arka planda, aktivite seçilince daha soluk) */}
          <Polyline
            positions={locationRoute.map((log) => [
              parseFloat(log.latitude),
              parseFloat(log.longitude)
            ])}
            pathOptions={{
              color: '#8c8c8c',
              weight: 2,
              opacity: selectedActivityId === null ? 0.7 : 0.2,
              dashArray: '6, 4'
            }}
          />
          
          {/* Aktivite marker'ları */}
          {activities.map(activity => {
            if (!isActivityVisible(activity.id)) return null; // Filtre kontrolü
            
            return activity.locations?.map((location, idx) => {
              const activityColor = getActivityColor(activity.id);
              const isSelected = selectedActivityId === activity.id;
              
              return (
                <Marker
                  key={`activity-${activity.id}-loc-${location.id}`}
                  position={[
                    parseFloat(location.latitude),
                    parseFloat(location.longitude)
                  ]}
                  icon={createActivityIcon(activityColor)}
                  opacity={isSelected ? 1 : 0.9}
                >
                  <Popup>
                    <div style={{ minWidth: '250px' }}>
                      <div className="mb-2">
                        <Tag color={activityColor}>{getActivityTypeLabel(activity.type)}</Tag>
                      </div>
                      <h6 className="mb-2">{activity.title}</h6>
                      <div className="small">
                        <div className="mb-1">
                          <ClockCircleOutlined className="me-1" />
                          {dayjs(location.timestamp).format('DD.MM.YYYY HH:mm:ss')}
                        </div>
                        <div className="mb-1">
                          📅 Başlangıç: {dayjs(activity.startDate).format('HH:mm')}
                        </div>
                        <div className="mb-1">
                          📅 Bitiş: {dayjs(activity.endDate).format('HH:mm')}
                        </div>
                        <div className="mt-2">
                          <Tag color={activity.status === 'COMPLETED' ? 'success' : activity.status === 'PLANNED' ? 'orange' : 'default'}>
                            {activity.status === 
                              'COMPLETED' ? 'Tamamlandı' 
                                : activity.status === 'PLANNED' ? 'Planlandı' 
                                : activity.status === 'CANCELLED' ? 'İptal Edildi' 
                                : activity.status}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            });
          })}

          {/* Route-only noktalar (aktiviteye ait olmayanlar) - Aktivite seçilince gizlenir */}
          {selectedActivityId === null && routeOnlyLocations.map((location, idx) => (
            <Marker
              key={`route-loc-${location.id}`}
              position={[
                parseFloat(location.latitude),
                parseFloat(location.longitude)
              ]}
              icon={routePointIcon}
              opacity={0.7}
            >
              <Popup>
                <div style={{ minWidth: '180px' }}>
                  <div className="mb-2">
                    <Tag color="default">Hareket Noktası</Tag>
                  </div>
                  <div className="small">
                    <div className="mb-1">
                      <ClockCircleOutlined className="me-1" />
                      <strong>Zaman:</strong>
                    </div>
                    <div className="mb-2">
                      {dayjs(location.timestamp).format('DD.MM.YYYY HH:mm:ss')}
                    </div>
                    <div className="mb-1">
                      <EnvironmentOutlined className="me-1" />
                      <strong>Konum:</strong>
                    </div>
                    <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      {parseFloat(location.latitude).toFixed(6)}, {parseFloat(location.longitude).toFixed(6)}
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* Başlangıç ve bitiş marker'ları */}
          {locationRoute.length > 0 && (
            <>
              <Marker
                position={[
                  parseFloat(locationRoute[0].latitude),
                  parseFloat(locationRoute[0].longitude)
                ]}
                icon={greenIcon}
              >
                <Popup>
                  <div>
                    <strong style={{ color: 'green' }}>🟢 Gün Başlangıcı</strong>
                    <br />
                    <Text strong>{dayjs(locationRoute[0].timestamp).format('HH:mm:ss')}</Text>
                  </div>
                </Popup>
              </Marker>
              
              {locationRoute.length > 1 && (
                <Marker
                  position={[
                    parseFloat(locationRoute[locationRoute.length - 1].latitude),
                    parseFloat(locationRoute[locationRoute.length - 1].longitude)
                  ]}
                  icon={redIcon}
                >
                  <Popup>
                    <div>
                      <strong style={{ color: 'red' }}>🔴 Son Konum</strong>
                      <br />
                      <Text strong>{dayjs(locationRoute[locationRoute.length - 1].timestamp).format('HH:mm:ss')}</Text>
                    </div>
                  </Popup>
                </Marker>
              )}
            </>
          )}
        </MapContainer>
      </div>
    </Card>
  );
};

export default RouteMap;

